import { isSpaceLayout, type ModuleLayouts } from '@/types/spaceLayout'

/**
 * The module catalog as `GET /spaces/modules` returns it: categories in display
 * order, each with module keys. The API sends keys only; every label comes from
 * `common:modules.*` in the app's locales.
 */
export interface ModuleCatalogEntry {
  key: string
  available: boolean
  /** F-34: the extension this module belongs to is not in the community's plan. */
  locked?: boolean
  /** F-198: the layouts the module offers and its default; absent for a module without the choice. */
  layouts?: ModuleLayouts | null
}

export interface ModuleCatalogCategory {
  key: string
  /** F-34: an extension category (`people`, `coworking`) the plan does not include. */
  locked?: boolean
  modules: ModuleCatalogEntry[]
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

/** `layouts: { options, default }` of a module entry, or `null` when it is missing or malformed. */
export const parseModuleLayouts = (value: unknown): ModuleLayouts | null => {
  if (!isRecord(value) || !Array.isArray(value.options)) return null
  const options = value.options.filter(isSpaceLayout)
  if (options.length === 0) return null
  const fallback = isSpaceLayout(value.default) && options.includes(value.default) ? value.default : options[0]
  return { options, default: fallback }
}

/**
 * Pulls the categories out of the response, tolerating both the native payload
 * and a leftover `{ data }` envelope. Anything malformed yields `null`, which
 * the caller treats like a failed request.
 */
export const parseModuleCatalog = (response: unknown): ModuleCatalogCategory[] | null => {
  if (!isRecord(response)) return null
  const envelope = isRecord(response.data) ? response.data : null
  const raw = response.categories ?? envelope?.categories
  if (!Array.isArray(raw)) return null

  const categories = raw.flatMap((c: unknown): ModuleCatalogCategory[] => {
    if (!isRecord(c) || typeof c.key !== 'string' || !Array.isArray(c.modules)) return []
    const categoryLocked = c.locked === true
    const modules = c.modules.flatMap((m: unknown): ModuleCatalogEntry[] =>
      isRecord(m) && typeof m.key === 'string'
        ? [{ key: m.key, available: m.available === true, locked: categoryLocked || m.locked === true, layouts: parseModuleLayouts(m.layouts) }]
        : []
    )
    return [{ key: c.key, locked: categoryLocked, modules }]
  })

  return categories.length > 0 ? categories : null
}

/**
 * The catalog of the session, per community: fetched the first time the picker
 * opens and kept, so reopening "Create space" draws the final layout at once.
 * Only a parsed answer is kept; a failure is retried on the next open.
 */
const catalogCache = new Map<string, ModuleCatalogCategory[] | null>()
const inFlight = new Map<string, Promise<ModuleCatalogCategory[] | null>>()

export const getCachedModuleCatalog = (communityId: string): ModuleCatalogCategory[] | null | undefined =>
  catalogCache.get(communityId)

export const loadModuleCatalog = (
  communityId: string,
  fetcher: () => Promise<unknown>
): Promise<ModuleCatalogCategory[] | null> => {
  if (catalogCache.has(communityId)) return Promise.resolve(catalogCache.get(communityId) ?? null)
  const pending = inFlight.get(communityId)
  if (pending) return pending

  const request = fetcher()
    .then((response) => {
      const parsed = parseModuleCatalog(response)
      if (parsed) catalogCache.set(communityId, parsed)
      return parsed
    })
    .finally(() => inFlight.delete(communityId))
  inFlight.set(communityId, request)
  return request
}

/** F-198: what a module offers in "Exibição", out of a loaded catalog; `null` hides the field. */
export const moduleLayoutsOf = (catalog: ModuleCatalogCategory[] | null | undefined, key: string): ModuleLayouts | null => {
  for (const category of catalog ?? []) {
    const entry = category.modules.find((m) => m.key === key)
    if (entry) return entry.layouts ?? null
  }
  return null
}
